// src/platform/dashboards/primitives.jsx
//
// Chart and stat primitives shared by every AI dashboard.
//
// These exist so a second dashboard does not re-invent a bar. They take plain
// numbers and a semantic `tone` — never a colour — so the admin theme keeps
// ownership of the palette and a dashboard keeps ownership of the meaning.
//
// Everything here is presentational: no fetching, no stores, no route
// awareness. A primitive that needed any of those would belong in the dashboard
// that uses it.

import React from "react";
import { TONE, toneOf, fmt, pct } from "./format";

// ── Stat ─────────────────────────────────────────────────────────────────────

export function Stat({ label, value, sub, tone = "neutral", big = false }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-fg-secondary font-medium">
        {label}
      </div>
      <div
        className={`mt-1 tabular-nums font-semibold tracking-tight ${
          big ? "text-3xl" : "text-xl"
        } ${tone === "neutral" ? "text-fg-primary" : toneOf(tone).text}`}
      >
        {value}
      </div>
      {sub ? <div className="mt-0.5 text-[11px] text-fg-secondary">{sub}</div> : null}
    </div>
  );
}

// ── SegmentedBar ─────────────────────────────────────────────────────────────
//
// One bar, several proportional segments. Segments carry a 2px gap so adjacent
// fills read as separate quantities rather than one blended block — the same
// reason a stacked chart needs a surface gap.

export function SegmentedBar({ segments, total, height = "h-4" }) {
  const sum = total ?? segments.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div className={`flex ${height} gap-[2px] rounded overflow-hidden bg-bg-softer`}>
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <div
            key={s.key}
            className={toneOf(s.tone).fill}
            style={{ width: `${pct(s.value, sum)}%` }}
            title={`${s.label ?? s.key} · ${fmt(s.value)} · ${pct(s.value, sum).toFixed(1)}%`}
          />
        ))}
    </div>
  );
}

// ── MetricRow ────────────────────────────────────────────────────────────────
//
// label / bar / value, aligned down a list. The grid columns are fixed so every
// row's bar starts and ends on the same pixel — a bar whose left edge moves with
// its label length is not comparable to the row above it.

export function MetricRow({ label, sublabel, segments, total, value }) {
  return (
    <div className="grid grid-cols-[minmax(96px,150px)_1fr_64px] gap-3 items-center py-2 border-t border-bg-border first:border-t-0">
      <div className="min-w-0">
        <div className="text-xs text-fg-primary truncate">{label}</div>
        {sublabel ? (
          <div className="text-[10px] text-fg-secondary tabular-nums">{sublabel}</div>
        ) : null}
      </div>
      <SegmentedBar segments={segments} total={total} />
      <div className="text-right text-xs tabular-nums text-fg-secondary">{value}</div>
    </div>
  );
}

// ── ColumnChart ──────────────────────────────────────────────────────────────
//
// Vertical stacked columns. Rendered as SVG so the bars keep their proportions
// when the panel is narrow, with the total direct-labelled above each column —
// the labels are what make the series readable without reading the axis, and
// they are the relief for series colours that sit low against this surface.

export function ColumnChart({ data, seriesColor, height = 230, maxOverride }) {
  const W = 700;
  const X0 = 46, X1 = 688, Y0 = 20, Y1 = height - 44;

  const totals = data.map((d) => d.parts.reduce((s, p) => s + p.value, 0));
  const peak = maxOverride ?? Math.max(1, ...totals);
  // Round the axis up to something a person would choose, so gridlines land on
  // readable numbers instead of the data's own maximum.
  const step = Math.pow(10, Math.floor(Math.log10(peak))) / 2;
  const max = Math.ceil(peak / step) * step;

  const slot = (X1 - X0) / Math.max(1, data.length);
  const bw = Math.min(70, slot - 26);
  const y = (v) => Y1 - (v / max) * (Y1 - Y0);

  const ticks = [0, max / 2, max];

  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="w-full h-auto overflow-visible" role="img"
         aria-label={data.map((d, i) => `${d.label} ${totals[i]}`).join(", ")}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={X0} x2={X1} y1={y(t)} y2={y(t)}
                stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          <text x={X0 - 8} y={y(t) + 4} textAnchor="end" fill="#94a3b8"
                fontSize="10.5" fontFamily="'IBM Plex Mono', ui-monospace, monospace">
            {fmt(t)}
          </text>
        </g>
      ))}

      {data.map((d, i) => {
        const x = X0 + slot * i + (slot - bw) / 2;
        let cursor = Y1;
        const rects = d.parts
          .filter((p) => p.value > 0)
          .map((p) => {
            const h = Math.max(2, (p.value / max) * (Y1 - Y0));
            cursor -= h;
            const rect = (
              <rect key={p.name} x={x} y={cursor} width={bw} height={h} rx="2"
                    fill={seriesColor(p.name)}>
                <title>{`${d.label} · ${p.name} · ${fmt(p.value)}`}</title>
              </rect>
            );
            cursor -= 2; /* surface gap between stacked segments */
            return rect;
          });

        return (
          <g key={d.label}>
            {rects}
            <text x={x + bw / 2} y={cursor - 6} textAnchor="middle" fill="#e2e8f0"
                  fontSize="11.5" fontWeight="500" fontFamily="'IBM Plex Mono', ui-monospace, monospace">
              {fmt(totals[i])}
            </text>
            <text x={x + bw / 2} y={Y1 + 18} textAnchor="middle" fill="#94a3b8"
                  fontSize="11">
              {d.label}
            </text>
          </g>
        );
      })}

      <line x1={X0} x2={X1} y1={Y1} y2={Y1} stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
    </svg>
  );
}

// ── MiniBars ─────────────────────────────────────────────────────────────────

export function MiniBars({ data, color = TONE.neutral.hex }) {
  const W = 320, H = 132, X0 = 30, X1 = 312, Y0 = 12, Y1 = 100;
  const max = Math.max(1, ...data.map((d) => d.value));
  const slot = (X1 - X0) / Math.max(1, data.length);
  const bw = Math.min(18, slot - 4);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto overflow-visible" role="img"
         aria-label={data.map((d) => `${d.label} ${d.value}`).join(", ")}>
      <line x1={X0} x2={X1} y1={Y1} y2={Y1} stroke="rgba(255,255,255,0.14)" />
      <text x={X0 - 6} y={Y0 + 4} textAnchor="end" fill="#94a3b8" fontSize="9.5"
            fontFamily="'IBM Plex Mono', ui-monospace, monospace">{fmt(max)}</text>
      {data.map((d, i) => {
        const h = Math.max(1.5, (d.value / max) * (Y1 - Y0));
        const x = X0 + slot * i + (slot - bw) / 2;
        return (
          <g key={d.label}>
            <rect x={x} y={Y1 - h} width={bw} height={h} rx="2" fill={color}>
              <title>{`${d.label} · ${fmt(d.value)}`}</title>
            </rect>
            <text x={x + bw / 2} y={Y1 + 14} textAnchor="middle" fill="#94a3b8"
                  fontSize="9.5" fontFamily="'IBM Plex Mono', ui-monospace, monospace">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Panel / DataTable ────────────────────────────────────────────────────────

export function Panel({ title, sub, right, children, className = "" }) {
  return (
    <section className={`rounded-lg border border-bg-border bg-bg-soft ${className}`}>
      <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
          {sub ? <p className="text-[11px] text-fg-secondary mt-0.5">{sub}</p> : null}
        </div>
        {right}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function DataTable({ columns, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={c.key}
                  className={`py-2 px-2 font-medium font-mono uppercase tracking-wider text-[10px]
                              text-fg-secondary border-b border-bg-border whitespace-nowrap
                              ${i === 0 ? "text-left" : "text-right"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-b border-bg-border last:border-b-0">
              {columns.map((c, i) => (
                <td key={c.key}
                    className={`py-2 px-2 whitespace-nowrap
                                ${i === 0 ? "text-left text-fg-primary" : "text-right tabular-nums text-fg-secondary"}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Legend ───────────────────────────────────────────────────────────────────

export function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-fg-secondary mb-3">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <i className="w-2.5 h-2.5 rounded-sm shrink-0"
             style={{ background: i.color }} aria-hidden="true" />
          {i.label}
        </span>
      ))}
    </div>
  );
}
